import { useLazyGetLocationsQuery } from '@/store/api/services/locations/locations';
import { useLazyGetMachinesQuery } from '@/store/api/services/machines/machines';
import type { Machine } from '@/types';
import type { Location } from '@/types/location';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import startCase from 'lodash/startCase';
import type React from 'react';
import { useEffect, useState } from 'react';
import type {
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { MAX_BULK_TARGETS, type TargetChip, type TargetType } from './targetChip';

interface TargetChipInputProps<TFieldValues extends FieldValues> {
  errors: FieldErrors<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  // Preserves the exact data-test selectors each form used before this component was shared.
  dataTestPrefix?: string;
}

const typeLabel = (type: TargetType | null) =>
  type === 'atm' ? 'Machine' : type === 'location' ? 'Location' : null;

const buildMachinePatch = (value: string, machine: Machine): Partial<TargetChip> => ({
  type: 'atm',
  status: 'valid',
  resolvedLocationId: machine.location_id ?? null,
  preview: {
    title: machine.name || `Machine ${value}`,
    subtitle: machine.location?.name || machine.location?.full_address || undefined,
    meta: machine.status ? startCase(machine.status) : undefined,
  },
});

const buildLocationPatch = (value: string, location: Location): Partial<TargetChip> => {
  const installedAtmCount = (location.installed_atms || []).filter(
    (atm) => !atm.date_time_uninstalled,
  ).length;
  return {
    type: 'location',
    status: 'valid',
    preview: {
      title: location.location_name || location.business_name || `Location ${value}`,
      subtitle:
        location.full_address ||
        [location.street_address, location.city, location.state].filter(Boolean).join(', ') ||
        undefined,
      meta: `${installedAtmCount} ATM${installedAtmCount === 1 ? '' : 's'}`,
    },
  };
};

const buildOrFilter = (property: string, ids: number[]) =>
  ids.length === 1
    ? { property, operator: 'equal', value: ids[0].toString() }
    : {
        operator: 'or',
        operands: ids.map((id) => ({ property, operator: 'equal', value: id.toString() })),
      };

export function TargetChipInput<TFieldValues extends FieldValues & { targets: TargetChip[] }>({
  errors,
  setValue,
  watch,
  dataTestPrefix = 'target-chip',
}: TargetChipInputProps<TFieldValues>) {
  const [inputValue, setInputValue] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);

  const [fetchMachines] = useLazyGetMachinesQuery();
  const [fetchLocations] = useLazyGetLocationsQuery();

  const targetsPath = 'targets' as Path<TFieldValues>;
  const targets = (watch(targetsPath) || []) as unknown as TargetChip[];

  const applyPatches = (patchByValue: Map<string, Partial<TargetChip>>) => {
    const current = (watch(targetsPath) || []) as unknown as TargetChip[];
    const next = current.map((target) =>
      patchByValue.has(target.value) ? { ...target, ...patchByValue.get(target.value) } : target,
    );
    setValue(targetsPath, next as unknown as PathValue<TFieldValues, Path<TFieldValues>>, {
      shouldValidate: true,
    });
  };

  // Resolves any number of machine/location IDs in at most 2 requests total (one "or" filtered
  // machines lookup, one "or" filtered locations lookup) instead of firing a request per ID -
  // a bulk selection of up to MAX_BULK_TARGETS IDs would otherwise fan out into that many requests.
  const fetchMachinesByIds = async (ids: number[]): Promise<Map<number, Machine>> => {
    if (ids.length === 0) return new Map();
    const machines = await fetchMachines({
      pagination: {
        current_page: 1,
        items_per_page: ids.length,
        sort_by: 'id',
        sort_ascending: true,
      },
      filter: buildOrFilter('id', ids),
    })
      .unwrap()
      .then((res) => res?.data ?? [])
      .catch(() => []);
    return new Map(machines.map((machine): [number, Machine] => [machine.id, machine]));
  };

  const fetchLocationsByIds = async (ids: number[]): Promise<Map<number, Location>> => {
    if (ids.length === 0) return new Map();
    const locations = await fetchLocations({
      pagination: {
        current_page: 1,
        items_per_page: ids.length,
        sort_by: 'location_id',
        sort_ascending: true,
      },
      filter: buildOrFilter('location_id', ids),
    })
      .unwrap()
      .then((res) => res?.data ?? [])
      .catch(() => []);
    return new Map(
      locations.map((location): [number, Location] => [location.location_id, location]),
    );
  };

  // A raw numeric ID could be a machine or a location - try both and tag the chip with
  // whichever one resolves, so the user never has to say which kind it is. When the caller
  // already knows the type (e.g. a bulk selection from a Machines/Locations/Cases table),
  // only that lookup runs instead of guessing between the two.
  const resolvePending = async (pending: { value: string; type?: TargetType | null }[]) => {
    if (pending.length === 0) return;

    const knownAtmIds = pending
      .filter((target) => target.type === 'atm')
      .map((target) => Number.parseInt(target.value, 10));
    const knownLocationIds = pending
      .filter((target) => target.type === 'location')
      .map((target) => Number.parseInt(target.value, 10));
    const unknown = pending.filter((target) => !target.type);
    const unknownIds = unknown.map((target) => Number.parseInt(target.value, 10));

    const [knownMachines, knownLocations, unknownMachines, unknownLocations] = await Promise.all([
      fetchMachinesByIds(knownAtmIds),
      fetchLocationsByIds(knownLocationIds),
      fetchMachinesByIds(unknownIds),
      fetchLocationsByIds(unknownIds),
    ]);

    const patchByValue = new Map<string, Partial<TargetChip>>();

    for (const target of pending) {
      const id = Number.parseInt(target.value, 10);
      if (target.type === 'atm') {
        const machine = knownMachines.get(id);
        patchByValue.set(
          target.value,
          machine
            ? buildMachinePatch(target.value, machine)
            : { type: null, status: 'invalid', error: `Machine ID ${target.value} not found` },
        );
      } else if (target.type === 'location') {
        const location = knownLocations.get(id);
        patchByValue.set(
          target.value,
          location
            ? buildLocationPatch(target.value, location)
            : { type: null, status: 'invalid', error: `Location ID ${target.value} not found` },
        );
      } else {
        const machine = unknownMachines.get(id);
        const location = unknownLocations.get(id);
        if (machine) {
          patchByValue.set(target.value, buildMachinePatch(target.value, machine));
        } else if (location) {
          patchByValue.set(target.value, buildLocationPatch(target.value, location));
        } else {
          patchByValue.set(target.value, {
            type: null,
            status: 'invalid',
            error: `ID ${target.value} doesn't match a machine or a location`,
          });
        }
      }
    }

    applyPatches(patchByValue);
  };

  // Resolve any targets that arrived pre-filled (e.g. a bulk selection from a table)
  // still sitting in "validating" state, once on mount.
  useEffect(() => {
    const pending = targets.filter((target) => target.status === 'validating');
    resolvePending(pending.map((target) => ({ value: target.value, type: target.type })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commitTokens = (raw: string) => {
    const tokens = raw
      .split(/[\s,]+/)
      .map((token) => token.trim())
      .filter(Boolean);

    if (tokens.length === 0) return;

    const newWarnings: string[] = [];
    const toAdd: TargetChip[] = [];

    for (const token of tokens) {
      if (!/^\d+$/.test(token)) {
        newWarnings.push(`"${token}" is not a valid numeric ID and was skipped`);
        continue;
      }
      const alreadyPresent =
        targets.some((target) => target.value === token) ||
        toAdd.some((target) => target.value === token);
      if (alreadyPresent) {
        newWarnings.push(`ID ${token} is already added`);
        continue;
      }
      if (targets.length + toAdd.length >= MAX_BULK_TARGETS) {
        newWarnings.push(
          `Only ${MAX_BULK_TARGETS} IDs are allowed per request - "${token}" was not added`,
        );
        continue;
      }
      toAdd.push({ type: null, value: token, status: 'validating' });
    }

    setWarnings(newWarnings);
    setInputValue('');

    if (toAdd.length === 0) return;

    setValue(
      targetsPath,
      [...targets, ...toAdd] as unknown as PathValue<TFieldValues, Path<TFieldValues>>,
      { shouldValidate: true },
    );
    resolvePending(toAdd.map((chip) => ({ value: chip.value, type: undefined })));
  };

  const removeTarget = (index: number) => {
    setValue(
      targetsPath,
      targets.filter((_, i) => i !== index) as unknown as PathValue<
        TFieldValues,
        Path<TFieldValues>
      >,
      { shouldValidate: true },
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitTokens(inputValue);
    } else if (event.key === 'Backspace' && !inputValue && targets.length > 0) {
      removeTarget(targets.length - 1);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const pasted = event.clipboardData.getData('text');
    if (/[\s,]/.test(pasted.trim())) {
      event.preventDefault();
      commitTokens(pasted);
    }
  };

  const targetsError = (errors as Record<string, { message?: string } | undefined>).targets;
  const previewableTargets = targets.filter(
    (target) => target.status === 'valid' && target.preview,
  );

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={() => commitTokens(inputValue)}
        placeholder="Type a machine or location ID and press Enter (paste multiple at once)"
        error={!!targetsError}
        helperText={
          targetsError?.message ||
          'Press Enter to add an ID. Paste a list separated by commas, spaces, or new lines.'
        }
        data-test={`${dataTestPrefix}-input`}
      />

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setWarnings([])}>
          {warnings.map((warning) => (
            <Typography key={warning} variant="body2">
              {warning}
            </Typography>
          ))}
        </Alert>
      )}

      {targets.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {targets.map((target, index) => {
            const isValidating = target.status === 'validating';
            const isInvalid = target.status === 'invalid';
            const label = typeLabel(target.type);
            return (
              <Chip
                key={target.value}
                label={label ? `${label} ${target.value}` : target.value}
                color={isInvalid ? 'error' : target.type === 'atm' ? 'success' : 'primary'}
                variant={isValidating ? 'outlined' : 'filled'}
                icon={isValidating ? <CircularProgress size={14} /> : undefined}
                onDelete={() => removeTarget(index)}
                deleteIcon={<CloseIcon />}
                title={isInvalid ? target.error : undefined}
                data-test={`${dataTestPrefix}-${target.value}`}
              />
            );
          })}
        </Box>
      )}

      {targets.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {targets.filter((target) => target.type === 'atm').length} machine ID(s),{' '}
          {targets.filter((target) => target.type === 'location').length} location ID(s) —{' '}
          {targets.filter((target) => target.status === 'valid').length} of {targets.length}{' '}
          validated
        </Typography>
      )}

      {previewableTargets.length > 0 && (
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewableTargets.map((target) => (
                <TableRow key={target.value}>
                  <TableCell>{typeLabel(target.type)}</TableCell>
                  <TableCell>{target.value}</TableCell>
                  <TableCell>{target.preview?.title}</TableCell>
                  <TableCell>
                    {target.preview?.subtitle && (
                      <Typography variant="body2">{target.preview.subtitle}</Typography>
                    )}
                    {target.preview?.meta && (
                      <Typography variant="caption" color="text.secondary">
                        {target.preview.meta}
                      </Typography>
                    )}
                    {!target.preview?.subtitle && !target.preview?.meta && '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
